/**
 * Purpose:
 *   Provides the single application-level PageState boundary for protected pages.
 *
 * Notes:
 *   - Pages publish state through usePageState instead of rendering PageState directly.
 *   - The boundary resets to ready when the protected route changes.
 */

'use client';

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import type { ReactNode } from 'react';
import { usePathname } from 'next/navigation';
import { PageState } from './PageState';
import type { IPageStateProps, TPageState } from './PageState.types';

export type IPageStateConfig = Omit<IPageStateProps, 'children' | 'state'> & {
  state: TPageState;
};

interface IPageStateContextValue {
  setPageState: (config: IPageStateConfig) => void;
  setPageHeader: (header: ReactNode) => void;
  resetPageState: () => void;
}

const PageStateContext = createContext<IPageStateContextValue | null>(null);

export function PageStateProvider({ children }: { children: React.ReactNode }) {
  const pathname = usePathname();
  const initialPathnameRef = useRef(pathname);
  const [pageState, setPageStateValue] = useState<IPageStateConfig>({
    state: 'ready',
    content: {},
  });
  const [pageHeader, setPageHeaderValue] = useState<ReactNode>(null);

  const resetPageState = useCallback(() => {
    setPageStateValue({ state: 'ready', content: {} });
  }, []);

  useEffect(() => {
    if (initialPathnameRef.current === pathname) {
      return;
    }

    initialPathnameRef.current = pathname;
    resetPageState();
  }, [pathname, resetPageState]);

  const setPageState = useCallback((config: IPageStateConfig) => {
    setPageStateValue(config);
  }, []);

  const setPageHeader = useCallback((header: ReactNode) => {
    setPageHeaderValue(header);
  }, []);

  const contextValue = useMemo(
    () => ({ setPageState, setPageHeader, resetPageState }),
    [resetPageState, setPageHeader, setPageState]
  );

  return (
    <PageStateContext.Provider value={contextValue}>
      {pageHeader}
      <PageState {...pageState}>{children}</PageState>
    </PageStateContext.Provider>
  );
}

export function PageStateHeader({ children }: { children: ReactNode }) {
  const { setPageHeader } = usePageState();

  useLayoutEffect(() => {
    setPageHeader(children);
    return () => setPageHeader(null);
  }, [children, setPageHeader]);

  return null;
}

export function usePageState() {
  const context = useContext(PageStateContext);

  if (!context) {
    throw new Error('usePageState must be used within PageStateProvider');
  }

  return context;
}
